import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { Alert, Box, Button, Grid } from '@mui/material';
import { Stack } from '@mui/system';

import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';

import { FORM_ERROR } from 'final-form';
import { Form } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat';

import { breadcrumbsActions } from '@store/breadcrumbs';
import { showSuccessMessage } from '@store/dashboard';
import { selectIsUserAdmin, selectLoggedInUserEmail } from '@store/auth';

import ApplicationPageTemplate from '@dashboardComponents/ApplicationPageTemplate/ApplicationPageTemplate';
import FormSaveAndSubmit from '@components/FormSaveAndSubmit';
import { Typography } from '@components/styledComponents';
import ConfirmationDialog from '@components/ConfirmationDialog';
import ProdConfirmationDialog from '@components/ProdConfirmationDialog';

import {
  getCurrencies,
  selectCurrencies,
} from '../../../payment-options/store/payment-options-currencies';

import {
  addRule,
  deleteRule,
  forceDisableRule,
  getRuleDetails,
  paymentOrchestrationRulesActions,
  selectSelectedOrchestrationRule,
  updateRule,
} from '../../store/payment-orchestration-rules.slice';
import {
  getRuleSources,
  selectRuleSources,
} from '../../store/orchestration-rules-sources.slice';
import {
  RULE_LIST_ROUTE,
  addRuleBreadcrumb,
  updateRuleBreadcrumb,
} from '../../payment-orchestration.breadcrumbs';
import {
  EFFICTIVITY_DATE_FORMAT,
  PAYMENT_ORCHESTRATION_RULE_STATUS,
} from '../../payment-orchestration.constants';
import {
  convertWeightageArrayToMap,
  getPercentageTotal,
} from '../../payment-orchestration.utils';

import WeightageForm from './components/WeightageForm';
import CollectionStatus from './components/CollectionStatus';
import DetailsForm from './components/DetailsForm';

const initForm = {
  source: '',
  monthlyCap: 0,
  transactionRatio: 0,
  weightageType: 'PERCENTAGE',
  settlementCurrency: 'USD',
  currencyWeightage: [
    {
      currency: 'MYR',
      percentage: 100,
    },
  ],
  effectiveStartDate: dayjs(Date.now()).add(1, 'day'),
  effectiveEndDate: dayjs(Date.now()).add(1, 'day'),
};

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);

export default function RuleForm({ permissions = {} }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const selectedRule = useSelector(selectSelectedOrchestrationRule);
  const userEmail = useSelector(selectLoggedInUserEmail);
  const isAdminUser = useSelector(selectIsUserAdmin);
  const sources = useSelector(selectRuleSources);
  const currencies = useSelector(selectCurrencies);
  const [openDialog, setOpenDialog] = useState({ show: false });

  const { id } = useParams();
  const [isEdit, setIsEditMode] = useState(id !== undefined);
  const [isFetchingData, setIsFetchingData] = useState(true);
  const [formValue, setFormValue] = useState(initForm);
  const [formSuccessMsg, setFormSuccessMsg] = useState('');
  const [formErrorMsg, setFormErrorMsg] = useState('');
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState({
    show: false,
  });
  const [showForceDisableConfirmation, setShowForceDisableConfirmation] =
    useState({
      show: false,
    });

  useEffect(() => {
    if (!permissions?.canViewPaymentOrchRule) {
      navigate('/landing');
    }
  }, [navigate, permissions]);

  useEffect(() => {
    setIsEditMode(id !== undefined);
  }, [id]);

  useEffect(() => {
    setIsFetchingData(true);
    dispatch(breadcrumbsActions.showMenu(true));

    dispatch(getCurrencies());
    dispatch(getRuleSources());
    dispatch(paymentOrchestrationRulesActions.resetCollectedCommission());

    // CREATE RULE
    if (!isEdit) {
      dispatch(breadcrumbsActions.setBreadcrumbs(addRuleBreadcrumb));
      Promise.all([
        dispatch(paymentOrchestrationRulesActions.resetSelectedRule()),
      ]).then(() => setIsFetchingData(false));

      return;
    }

    // EDIT RULE
    dispatch(breadcrumbsActions.setBreadcrumbs(updateRuleBreadcrumb(id)));
    Promise.all([dispatch(getRuleDetails(id))]).then(() =>
      setIsFetchingData(false),
    );
  }, [id, dispatch, isEdit]);

  useEffect(() => {
    if (isEdit) {
      if (!selectedRule) return;
      const formatted = {
        ...selectedRule,
        effectiveStartDate: dayjs(selectedRule.effectiveStartDate),
        effectiveEndDate: dayjs(selectedRule.effectiveEndDate),
        currencyWeightage: Object.entries(selectedRule.currencyWeightage).map(
          ([key, value]) => ({ currency: key, percentage: value }),
        ),
      };
      setFormValue(formatted);
    } else {
      setFormValue(initForm);
    }
  }, [id, isEdit, selectedRule]);

  const validate = (values) => {
    if (!values.source) return { [FORM_ERROR]: 'Source is required' };
    if (!values.settlementCurrency)
      return { [FORM_ERROR]: 'Monthly cap currency is required' };
    if (!values.monthlyCap) return { [FORM_ERROR]: 'Monthly cap is required' };
    if (values.monthlyCap < 1) return { [FORM_ERROR]: 'Monthly cap should ' };
    if (!values.transactionRatio)
      return { [FORM_ERROR]: 'Transaction routing ratio is required' };
    if (values.transactionRatio > 100 || values.transactionRatio < 1)
      return {
        [FORM_ERROR]: 'Transaction routing ratio is not within allowed range',
      };
    if (!values.effectiveStartDate)
      return { [FORM_ERROR]: 'Start date is required' };
    if (!values.effectiveEndDate)
      return { [FORM_ERROR]: 'End date is required' };
    if (values.weightageType === 'PERCENTAGE') {
      if (getPercentageTotal(values.currencyWeightage) !== 100)
        return { [FORM_ERROR]: 'Total weightage should be 100' };
      if (
        !values.currencyWeightage
          .filter((weight) => weight.percentage && weight.percentage > 0)
          .every((weight) => !!weight.currency)
      )
        return { [FORM_ERROR]: 'Weights should be valid' };
    }
    return {};
  };

  const onSubmit = useCallback(
    (values) => {
      setFormSuccessMsg('');
      setFormErrorMsg('');

      validate(values);

      const {
        source,
        monthlyCap,
        transactionRatio,
        settlementCurrency,
        weightageType,
        currencyWeightage,
        effectiveStartDate,
        effectiveEndDate,
      } = values;

      let payload = {};

      if (!isEdit) {
        payload = {
          source,
          channelHash: sources.find((s) => s.source === source).channelHash,
          settlementCurrency,
          monthlyCap: parseFloat(monthlyCap),
          transactionRatio: parseFloat(transactionRatio),
          weightageType,
          currencyWeightage: convertWeightageArrayToMap(currencyWeightage),
          settlementMethod: 'CREDIT_CARD', // NOTE: hard-coded for the time being
          effectiveStartDate: dayjs(effectiveStartDate).format(
            EFFICTIVITY_DATE_FORMAT,
          ),
          effectiveEndDate: dayjs(effectiveEndDate).format(
            EFFICTIVITY_DATE_FORMAT,
          ),
          user: userEmail,
        };
      } else {
        payload = {
          ...selectedRule,
          effectiveStartDate: dayjs(effectiveStartDate).format(
            EFFICTIVITY_DATE_FORMAT,
          ),
          effectiveEndDate: dayjs(effectiveEndDate).format(
            EFFICTIVITY_DATE_FORMAT,
          ),
          user: userEmail,
        };
      }

      return dispatch(!isEdit ? addRule(payload) : updateRule(payload)).then(
        ({ payload: response }) => {
          if (response && response.status === 'SUCCESS') {
            if (!isEdit) {
              dispatch(
                showSuccessMessage(
                  `Rule ${isEdit ? 'updated' : 'added'} successfully`,
                ),
              );
              navigate(RULE_LIST_ROUTE);
            } else {
              setFormSuccessMsg('Rule has been updated successfully');
              setFormValue(values); // needed to disable buttons after successful submit
            }
          } else {
            setFormErrorMsg(
              response?.errorMessage ||
                `Unable to ${isEdit ? 'update' : 'add'} rule`,
            );
          }
        },
      );
    },
    [isEdit, dispatch, sources, userEmail, selectedRule, navigate],
  );

  const deleteRuleHandler = () => {
    dispatch(deleteRule({ id, user: userEmail })).then((response) => {
      const { payload } = response;
      if (payload && payload.status === 'SUCCESS') {
        dispatch(showSuccessMessage('Rule deleted successfully'));
        navigate(RULE_LIST_ROUTE);
      } else {
        setFormErrorMsg(
          response?.payload?.errorMessage || 'Unable to delete rule',
        );
      }
    });
  };

  const forceDisableRuleHandler = () => {
    dispatch(forceDisableRule({ id, user: userEmail })).then((response) => {
      const { payload } = response;
      if (payload && payload.status === 'SUCCESS') {
        dispatch(
          showSuccessMessage('Rule status has been updated successfully'),
        );
        navigate(RULE_LIST_ROUTE);
      } else {
        setFormErrorMsg(
          response?.payload?.errorMessage || 'Unable to force disable rule',
        );
      }
    });
  };

  const clearNotification = () => {
    setFormSuccessMsg('');
    setFormErrorMsg('');
  };

  const renderFormNotification = () => (
    <Grid
      item
      xs={12}
      md={8}
      style={{ flex: 2 }}>
      {formSuccessMsg && (
        <Alert
          onClose={() => clearNotification()}
          severity="success">
          {formSuccessMsg}
        </Alert>
      )}
      {formErrorMsg && (
        <Alert
          onClose={() => clearNotification()}
          severity="error">
          {formErrorMsg}
        </Alert>
      )}
    </Grid>
  );

  const renderFormActions = ({
    pristine,
    submitting,
    invalid,
    form,
    permissions: userPerm,
  }) => (
    <Grid
      item
      xs={12}
      md={4}
      style={{ display: 'flex' }}>
      <FormSaveAndSubmit
        id={id}
        resetDisabled={pristine}
        submitDisabled={
          !userPerm.canCreatePaymentOrchRule ||
          !userPerm.canUpdatePaymentOrchRule ||
          submitting ||
          pristine ||
          invalid ||
          // TODO: remove once update implem is finalized
          isEdit
        }
        handleReset={() => {
          setFormSuccessMsg('');
          setFormErrorMsg('');
          form.reset();
        }}
      />
    </Grid>
  );

  const renderActions = (values) => {
    if (!isAdminUser || !isEdit) return null;

    return (
      <>
        <Typography
          className="form-header"
          variant="formHeader">
          Actions
        </Typography>
        <Stack
          spacing={2}
          maxWidth={200}>
          {values.status === PAYMENT_ORCHESTRATION_RULE_STATUS.ENABLED && (
            <Button
              variant="outlined"
              startIcon={<ToggleOffIcon />}
              disabled={!permissions?.canForceDisablePaymentOrchRule}
              onClick={() => {
                setShowForceDisableConfirmation({ show: true });
              }}>
              Force Disable
            </Button>
          )}
          {values.status !== PAYMENT_ORCHESTRATION_RULE_STATUS.ENABLED && (
            <Button
              variant="outlined"
              startIcon={<DeleteForeverIcon />}
              disabled={!permissions?.canDeletePaymentOrchRule}
              onClick={() => {
                setShowDeleteConfirmation({ show: true });
              }}>
              Delete Rule
            </Button>
          )}
        </Stack>
      </>
    );
  };

  const handlePreSubmit = (e) => {
    e.preventDefault();
    setOpenDialog({ show: true }); // open the confirmation dialog if prod
  };

  return (
    <ApplicationPageTemplate pageReady={!isFetchingData}>
      {!isFetchingData && (
        <>
          <Form
            onSubmit={onSubmit}
            initialValues={{ ...formValue }}
            validate={validate}
            mutators={{
              ...arrayMutators,
              setValue: ([field, value], state, { changeValue }) => {
                changeValue(state, field, () => value);
              },
            }}
            render={({
              handleSubmit,
              values,
              pristine,
              submitting,
              invalid,
              form,
              form: {
                mutators: { push },
              },
            }) => (
              <form
                onSubmit={(e) =>
                  process.env.REACT_APP_ENV !== 'production'
                    ? handleSubmit(e)
                    : handlePreSubmit(e)
                }
                noValidate>
                <ProdConfirmationDialog
                  openDialog={openDialog}
                  yesHandler={handleSubmit}
                />
                <Grid
                  container
                  spacing={1}
                  style={{
                    alignContent: 'center',
                    justifyContent: 'space-between',
                  }}>
                  {renderFormNotification()}
                  {renderFormActions({
                    form,
                    submitting,
                    pristine,
                    invalid,
                    values,
                    permissions,
                  })}
                </Grid>
                <Box>
                  <DetailsForm
                    id={id}
                    isEdit={isEdit}
                    values={values}
                  />
                  <WeightageForm
                    currencies={currencies}
                    isEdit={isEdit}
                    values={values}
                    push={push}
                  />
                  {isEdit && <CollectionStatus />}
                  {renderActions(values)}
                </Box>
              </form>
            )}
          />
          <ConfirmationDialog
            openDialog={showDeleteConfirmation}
            yesHandler={deleteRuleHandler}
            title="Delete"
            message="Are you sure you want to delete the this rule?"
          />
          <ConfirmationDialog
            openDialog={showForceDisableConfirmation}
            yesHandler={forceDisableRuleHandler}
            title="Force Disable"
            message="Are you sure you want to force disable this rule?"
          />
        </>
      )}
    </ApplicationPageTemplate>
  );
}
